import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import * as fragments from '~/apollo/fragments';
import type {
  OutcropReservoirModelsTabQuery,
  OutcropReservoirModelsTabQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { TaggedSupportingObjectList } from '~/components/supportingObject/TaggedSupportingObjectList';
import { useOutcropOutletContext } from '~/routes/outcrop/$outcropId';

const OUTCROP_RESERVOIR_MODELS_TAB = gql`
  query OutcropReservoirModelsTab($outcropId: Int!) {
    outcropList(id: $outcropId) {
      ...outcropParts
      reservoirModels {
        ...reservoirModelParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
            signedUrl
          }
        }
        files {
          ...fileParts
          signedUrl
        }
      }

      studies {
        ...studyParts

        dataHistory {
          ...dataHistoryParts
        }

        reservoirModels {
          ...reservoirModelParts
          pictures {
            ...pictureParts
            file {
              ...fileParts
              signedUrl
            }
          }
          files {
            ...fileParts
            signedUrl
          }
        }
      }
    }
  }

  ${fragments.outcropParts}
  ${fragments.reservoirModelParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.studyParts}
  ${fragments.dataHistoryParts}
`;

export default function OutcropReservoirModelsRoute() {
  const ctx = useOutcropOutletContext();
  const outcropId = ctx.outcrop.id;

  const { data, loading } = useQuery<
    OutcropReservoirModelsTabQuery,
    OutcropReservoirModelsTabQueryVariables
  >(OUTCROP_RESERVOIR_MODELS_TAB, { variables: { outcropId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: OUTCROP_RESERVOIR_MODELS_TAB, variables: { outcropId } },
  ];

  const outcrop = data?.outcropList.find(oc => oc.id === outcropId);
  const items = outcrop?.reservoirModels ?? [];
  const outcropStudies = R.sortBy(R.prop('name'), outcrop?.studies ?? []);

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <>
      <SupportingObjectList
        items={items}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.ReservoirModel}
        refetchQueries={refetchQueries}
      />

      <TaggedSupportingObjectList
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="reservoirModels"
        refetchQueries={refetchQueries}
      />
    </>
  );
}
